import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import styles from './MicrositeSeoPages.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, RefreshCw, Plus, Pencil } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import MicrositeTable from '../components/MicrositeTable/MicrositeTable'
import MicrositePagination from '../components/MicrositePagination/MicrositePagination'
import MicrositeSearchBar from '../components/MicrositeSearchBar/MicrositeSearchBar'
import MicrositeNoindexBadge from '../components/MicrositeNoindexBadge/MicrositeNoindexBadge'
import MicrositeDeleteConfirm from '../components/MicrositeDeleteConfirm/MicrositeDeleteConfirm'
import MicrositeInlineEditRow from '../components/MicrositeInlineEditRow/MicrositeInlineEditRow'
import TextInput from '../components/TextInput/TextInput'
import MicrositeSlider from '../components/MicrositeSlider/MicrositeSlider'

const EMPTY_PAGE = {
    pageSlug: '',
    pageTitle: '',
    pageDescription: '',
    noindex: false,
    nofollow: false,
    priority: 0.5
}

const PAGE_LIMIT = 20

const MicrositeSeoPages = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(true)
    const [toast, setToast] = useState(null)
    const [rows, setRows] = useState([])
    const [search, setSearch] = useState('')
    const [cursorStack, setCursorStack] = useState([0])
    const [nextCursor, setNextCursor] = useState(null)

    const [adding, setAdding] = useState(false)
    const [newPage, setNewPage] = useState(EMPTY_PAGE)
    const [savingNew, setSavingNew] = useState(false)

    const currentCursor = cursorStack[cursorStack.length - 1]

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: activeStore?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadPages = useCallback(async (cursor) => {
        setLoading(true)
        const res = await post('micrositeGetSeoPages', { limit: PAGE_LIMIT, cursor: cursor || 0 })
        if (res?.status === 'success') {
            setRows(res.response?.data ?? [])
            setNextCursor(res.response?.nextCursor ?? null)
        } else {
            showToast('Could not load SEO pages. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadPages(currentCursor)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, currentCursor])

    const filteredRows = useMemo(() => {
        if (!search.trim()) return rows
        const q = search.trim().toLowerCase()
        return rows.filter(
            (r) => r.pageSlug?.toLowerCase().includes(q) || r.pageTitle?.toLowerCase().includes(q)
        )
    }, [rows, search])

    const handleSaveNew = async () => {
        if (!newPage.pageSlug.trim()) {
            showToast('Page slug is required', 'error')
            return
        }
        setSavingNew(true)
        const res = await post('micrositeUpsertSeoPage', { ...newPage })
        setSavingNew(false)

        if (res?.status === 'success') {
            showToast('SEO page override saved')
            setAdding(false)
            setNewPage(EMPTY_PAGE)
            loadPages(currentCursor)
        } else {
            showToast('Could not save SEO page override', 'error')
        }
    }

    const handleDelete = async (ID) => {
        const res = await post('micrositeDeleteSeoPage', { ID })
        if (res?.status === 'success') {
            setRows((prev) => prev.filter((r) => r.ID !== ID))
            showToast('SEO page override deleted')
        } else {
            showToast('Could not delete', 'error')
        }
    }

    const columns = [
        {
            key: 'pageSlug',
            label: 'Page',
            render: (row) => (
                <div className={styles.pageCell}>
                    <span className={styles.slug}>{row.pageSlug}</span>
                    {row.pageTitle && <span className={styles.title}>{row.pageTitle}</span>}
                </div>
            )
        },
        {
            key: 'priority',
            label: 'Priority',
            render: (row) => <span className={styles.priorityValue}>{Number(row.priority).toFixed(1)}</span>
        },
        {
            key: 'flags',
            label: 'Flags',
            render: (row) => (
                <div className={styles.flagsCell}>
                    <MicrositeNoindexBadge noindex={row.noindex} />
                    {row.nofollow && <span className={styles.nofollowBadge}>nofollow</span>}
                </div>
            )
        },
        {
            key: 'actions',
            label: '',
            align: 'right',
            render: (row) => (
                <div className={styles.rowActions}>
                    <button type="button" className={styles.editBtn} onClick={() => showToast('Edit form coming via MicrositeSeoPageEditor', 'error')}>
                        <Pencil size={13} />
                    </button>
                    <MicrositeDeleteConfirm mode="inline" onConfirm={() => handleDelete(row.ID)} />
                </div>
            )
        }
    ]

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title="Page SEO"
                backTo="/microsite/seo"
                backLabel="SEO settings"
                subtitle="Override search metadata for specific pages."
                primaryAction={{ label: 'Add override', icon: <Plus size={14} />, onClick: () => setAdding(true) }}
            />

            <div className={styles.toolbar}>
                <MicrositeSearchBar placeholder="Search by slug or title…" onSearch={setSearch} />
            </div>

            {adding && (
                <MicrositeInlineEditRow onSave={handleSaveNew} onCancel={() => { setAdding(false); setNewPage(EMPTY_PAGE) }} saving={savingNew}>
                    <TextInput
                        label="Page slug"
                        value={newPage.pageSlug}
                        onChange={(v) => setNewPage((p) => ({ ...p, pageSlug: v }))}
                        placeholder="/about-us"
                    />
                    <TextInput
                        label="Page title"
                        value={newPage.pageTitle}
                        onChange={(v) => setNewPage((p) => ({ ...p, pageTitle: v }))}
                        placeholder="About Us — Glam Studio"
                    />
                </MicrositeInlineEditRow>
            )}

            <MicrositeTable
                columns={columns}
                rows={filteredRows}
                getRowKey={(row) => row.ID}
                loading={loading}
                emptyMessage="No page-level SEO overrides yet"
                emptyDescription="Add one to customize search metadata for a specific page"
            />

            <MicrositePagination
                hasPrev={cursorStack.length > 1}
                hasNext={!!nextCursor}
                onPrev={() => setCursorStack((s) => s.slice(0, -1))}
                onNext={() => setCursorStack((s) => [...s, nextCursor])}
                loading={loading}
                currentCount={filteredRows.length}
            />
        </div>
    )
}

export default MicrositeSeoPages